import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { CircularProgress, Dialog, DialogActions, DialogTitle, Divider, Grid } from '@material-ui/core'
import { Page, Header, CustomButton } from 'components'
import moment from 'moment'
import localization from 'moment/locale/pt-br'
import { useFormState } from 'hooks/useFormState'
import { useSolicitacaoExameService } from 'hooks/useSolicitacaoExameService'
import { useAuth } from 'hooks/useAuth'
import useRouter from 'utils/useRouter'
import useStyles from './EditarSolicitacao.styles'
import { FileManager } from 'components/FileManager'
import { useParams } from 'react-router'
import { FormBase } from '../Componentes/FormBase'
import { Confirmacao } from '../Componentes/Confirmacao'
import { ExamesSolicitados } from '../Componentes/ExamesSolicitados'
import { FormArquivos } from '../Componentes/FormArquivos'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SaveIcon from '@material-ui/icons/Save'
import DeleteIcon from '@material-ui/icons/Delete'

const schema = {
  autReq: {
    presence: { allowEmpty: false, message: '^Autoridade requisitante é obrigatório' },
  },
  setReq: {
    presence: { allowEmpty: false, message: '^Setor requisitante é obrigatório' },
  },
  setDest: {
    presence: { allowEmpty: false, message: '^Setor destino é obrigatório' },
  },
  data: {
    presence: { allowEmpty: false, message: '^Data é obrigatório' },
  },
  tipoDocumento: {
    presence: { allowEmpty: false, message: '^O tipo do documento da solicitação é obrigatório' },
  },
  numeroDocumento: {
    presence: { allowEmpty: false, message: '^O número do documento da solicitação é obrigatório' },
  },

  tipoProc: {
    presence: { allowEmpty: false, message: '^O tipo de procedimento é obrigatório' },
  },
}

const MainForm = (props) => {
  const router = useRouter()
  const { arquivos, route, idCaso, solicitacao, processandoSolicitacao, children, setorResp } = props
  moment.locale('pt-br', localization)
  const classes = useStyles()
  const { currentUser } = useAuth()
  const solicitacaoExameService = useSolicitacaoExameService()
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [isFormInvalid, setIsFormInvalid] = useState(false)
  const [openDeletarSolicitacao, setOpenDeletarSolicitacao] = useState(false)
  const [openEspelhoConfirmacao, setOpenEspelhoConfirmacao] = useState(false)

  const formSolicitacao = useFormState(schema, {
    setReq: setorResp,
    setDest: null,
    autReq: null,
    autoridadeRequisitanteInput: null,
    data: new Date(),
    tipoProc: null,
    numeroProcedimento: null,
    observacoes: null,
    tipoDocumento: null,
    numeroDocumento: null,
    descricaoDocumento: null,
    indiciado: null,
    boletimOcorrencia: null,
  })

  const [examesSolicitados, setExamesSolicitados] = useState([])

  const handleBotaoCadastrarEditar = useCallback(() => {
    setOpenEspelhoConfirmacao(true)
  }, [])

  const handleConfirmOperacao = useCallback(async () => {
    if (!isFormInvalid) {
      const dadosSolicitacaoExame = {
        id_autoridade_requisitante: formSolicitacao.formState.values.autReq?.id,
        identificador_caso_rip: formSolicitacao.formState.values.identificador_caso_rip,
        autoridadeRequisitanteInput: formSolicitacao.formState.values.autoridadeRequisitanteInput,
        id_setor_solicitante: formSolicitacao.formState.values.setReq?.id,
        id_setor_destino_laudo: formSolicitacao.formState.values.setDest?.id,
        data: formSolicitacao.formState.values.data,
        id_tipo_procedimento: formSolicitacao.formState.values.tipoProc?.id,
        numeroProcedimento: formSolicitacao.formState.values.numeroProcedimento,
        observacoes: formSolicitacao.formState.values.observacoes,
        tipoDocumento: formSolicitacao.formState.values.tipoDocumento,
        numeroDocumento: formSolicitacao.formState.values.numeroDocumento,
        descricaoDocumento: formSolicitacao.formState.values.descricaoDocumento,
        indiciado: formSolicitacao.formState.values.indiciado,
        boletimOcorrencia: formSolicitacao.formState.values.boletimOcorrencia,
        id_caso: idCaso,
        arquivos: formSolicitacao.formState.values.arquivos,
        exSols: examesSolicitados,
      }
      if (route.editar) {
        await solicitacaoExameService.editarSolicitacaoExame(solicitacao.id, dadosSolicitacaoExame, setProcessandoOperacao)
        router.history.push(`/solicitacaoexame/fichasolicitacaoexame/detalhar/${solicitacao.id}`)
      } else {
        let result = null
        if (route.origem === 'REQUISICAO') {
          result = await solicitacaoExameService.cadastrarSolicitacaoPorRequisicaoExame(dadosSolicitacaoExame, setProcessandoOperacao)
        } else {
          result = await solicitacaoExameService.cadastrarSolicitacaoExame(dadosSolicitacaoExame, setProcessandoOperacao)
        }
        router.history.push(`/casos/caso/detalhar/${dadosSolicitacaoExame.id_caso}`)
      }
    }
  }, [isFormInvalid, examesSolicitados, formSolicitacao.formState.values, idCaso, route.editar, solicitacao])

  const handleClickOpenAlertDelete = useCallback(async () => {
    setOpenDeletarSolicitacao(true)
  }, [])

  const handleClickCloseDeletarSolicitacao = useCallback(() => {
    setOpenDeletarSolicitacao(false)
  }, [])

  const handleClickConfirmDeletarSolicitacao = useCallback(async () => {
    await solicitacaoExameService.deletarSolicitacaoExame(solicitacao.id, setProcessandoOperacao)
    router.history.push(`/casos/caso/detalhar/${solicitacao.id_caso}`)
  }, [openDeletarSolicitacao, solicitacao])

  useEffect(() => {
    let formInvalid = false
    if (
      formSolicitacao.hasErrorWithoutTouch('setReq') ||
      formSolicitacao.hasErrorWithoutTouch('autReq') ||
      formSolicitacao.hasErrorWithoutTouch('setDest') ||
      formSolicitacao.hasErrorWithoutTouch('tipoProc') ||
      formSolicitacao.hasErrorWithoutTouch('tipoDocumento') ||
      formSolicitacao.hasErrorWithoutTouch('descricaoDocumento') ||
      (!formSolicitacao.formState.values.numeroDocumento && formSolicitacao.formState.values.tipoDocumento !== 'GUIA DE REQUISIÇÃO') ||
      (!formSolicitacao.formState.values.numeroProcedimento && formSolicitacao.formState.values.tipoProc?.nome?.toUpperCase() !== 'NÃO INFORMADO')
    ) {
      formInvalid = true
    }

    if (!formSolicitacao.formState.values.data || isNaN(formSolicitacao.formState.values.data)) {
      formInvalid = true
    }

    if (examesSolicitados.length < 1 && !route.editar) {
      formInvalid = true
    }

    setIsFormInvalid(formInvalid)
  }, [formSolicitacao, examesSolicitados])



  useEffect(() => {
    if (route.editar && solicitacao) {
      formSolicitacao.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          setReq: solicitacao.setReq,
          setDest: solicitacao.setDest,
          autReq: solicitacao.autReq,
          autoridadeRequisitanteInput: solicitacao.autoridadeRequisitanteInput,
          data: solicitacao.data ? new Date(solicitacao.data) : new Date(),
          tipoProc: solicitacao.tipoProc,
          numeroProcedimento: solicitacao.numeroProcedimento,
          observacoes: solicitacao.observacoes,
          tipoDocumento: solicitacao.tipoDocumento,
          numeroDocumento: solicitacao.numeroDocumento,
          descricaoDocumento: solicitacao.descricaoDocumento,
          indiciado: solicitacao.indiciado,
          boletimOcorrencia: solicitacao.boletimOcorrencia,
        },
      }))
    }
  }, [solicitacao])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} pathname={router.location.pathname} router={router} />

      {processandoSolicitacao ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <form id="form-solicitacao" onSubmit={(e) => e.preventDefault()}>
          <Grid container spacing={3}>
            <FormBase arquivos={arquivos} formSolicitacao={formSolicitacao} route={route} />
            {!route.editar && <ExamesSolicitados idCaso={idCaso} route={route} solicitacao={solicitacao} examesSolicitados={examesSolicitados} setExamesSolicitados={setExamesSolicitados} />}

            <FormArquivos arquivos={arquivos}>{children}</FormArquivos>
          </Grid>

          <Divider style={{ margin: '32px 0 16px 0' }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                <CustomButton variant="outlined" colorType="primary" startIcon={<ArrowBackIcon />} onClick={() => router.history.goBack()}>
                  Cancelar
                </CustomButton>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {currentUser.cargo?.id === 1 && route.editar && (
                    <CustomButton variant="contained" colorType="error" startIcon={<DeleteIcon />} onClick={handleClickOpenAlertDelete} disabled={processandoOperacao}>
                      Excluir
                    </CustomButton>
                  )}
                  <CustomButton variant="contained" colorType="secondary" startIcon={<SaveIcon />} disabled={isFormInvalid} isLoading={processandoOperacao} onClick={() => handleBotaoCadastrarEditar()}>
                    {route.editar ? 'Atualizar' : 'Cadastrar'}
                  </CustomButton>
                </div>
              </div>
            </Grid>
          </Grid>
        </form>
      )}
      <Confirmacao formSolicitacao={formSolicitacao} examesSolicitados={examesSolicitados} openEspelhoConfirmacao={openEspelhoConfirmacao} setOpenEspelhoConfirmacao={setOpenEspelhoConfirmacao} handleConfirmOperacao={handleConfirmOperacao} processandoOperacao={processandoOperacao} />

      <Dialog open={openDeletarSolicitacao} onClose={handleClickCloseDeletarSolicitacao} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Tem certeza que deseja deletar?</DialogTitle>
        <DialogActions style={{ padding: '20px' }}>
          <CustomButton onClick={handleClickCloseDeletarSolicitacao} colorType="primary" variant="outlined">
            Cancelar
          </CustomButton>
          <CustomButton onClick={() => handleClickConfirmDeletarSolicitacao()} colorType="secondary" variant="contained" isLoading={processandoOperacao}>
            Confirmar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

export const EditarSolicitacao = (props) => {
  const { route } = props
  const params = useParams()
  const router = useRouter()
  const solicitacaoExameService = useSolicitacaoExameService()
  const [solicitacao, setSolicitacao] = useState(null)
  const [processandoSolicitacao, setProcessandoSolicitacao] = useState(false)
  const [arquivos, setArquivos] = useState([])
  const arquivosIniciais = useMemo(() => (route.editar && solicitacao?.arqs?.map((arquivo) => arquivo.id)) || [], [solicitacao])

  const fetchSolicitacaoExame = useCallback(async () => {
    if (route.editar) {
      const response = await solicitacaoExameService.listarSolicitacaoExameAsync(params.id, setProcessandoSolicitacao)
      setSolicitacao(response)
    }
  }, [params.id, route.editar])

  useEffect(() => {
    fetchSolicitacaoExame()
  }, [fetchSolicitacaoExame])

  return (
    <MainForm processandoSolicitacao={processandoSolicitacao} idCaso={params.idCaso} arquivos={arquivos} route={route} setorResp={router.location.state?.setorResponsavel || null} solicitacao={solicitacao}>
      <FileManager rotaUpload="/arquivo/solicitacaoExame" rotaLoadImagem="/arquivo/solicitacaoExame" setArquivosPai={setArquivos} arquivosIniciais={arquivosIniciais} tipos={['image', 'pdf']} center />
    </MainForm>
  )
}
